import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Globe2, MessageCircle, Calendar, Image as ImageIcon, BarChart3 } from "lucide-react";
import DevBranding from "../components/DevBranding";

/**
 * HeavenlyEats — SaaS marketing landing page (placeholder).
 * Phase 5 will flesh out features / pricing / blog / docs.
 * For now this is a single-page hero that proves the new SaaS routing works
 * and gives prospects an immediate "try the demo" path to /r/delicias.
 */
const Marketing = () => (
    <div data-testid="marketing-page" className="min-h-screen bg-[#0f0f12] text-white relative overflow-hidden">
        {/* background gradient + grain */}
        <div className="absolute inset-0 z-0 opacity-50 pointer-events-none"
             style={{ background: "radial-gradient(ellipse at top left, #C85A1733 0%, transparent 60%), radial-gradient(ellipse at bottom right, #4A7C5933 0%, transparent 60%)" }} />
        <div className="absolute inset-0 z-0 opacity-[0.04] mix-blend-overlay pointer-events-none"
             style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />

        {/* Header */}
        <header className="relative z-10 px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center font-serif font-black text-white">
                    H
                </div>
                <div>
                    <div className="font-serif font-bold text-lg leading-none">HeavenlyEats</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">by HeavenlyAIsolutions</div>
                </div>
            </div>
            <nav className="hidden md:flex items-center gap-7 text-sm text-white/70">
                <a href="#features" className="hover:text-white transition-colors">Features</a>
                <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
                <Link to="/r/delicias" className="hover:text-white transition-colors">Demo</Link>
            </nav>
            <Link
                to="/r/delicias"
                data-testid="marketing-cta-demo-header"
                className="px-4 py-2 rounded-full bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors"
            >
                See the demo
            </Link>
        </header>

        {/* Hero */}
        <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pt-12 sm:pt-20 pb-24">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="max-w-3xl"
            >
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.2em] font-bold border border-white/15 bg-white/5 text-white/80">
                    <Sparkles className="w-3 h-3" /> Restaurant SaaS · Now in beta
                </span>
                <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mt-6 tracking-tight">
                    A beautiful restaurant site in <span className="text-orange-400">five minutes</span>.<br />
                    Orders straight to <span className="text-green-400">WhatsApp</span>.
                </h1>
                <p className="text-lg sm:text-xl text-white/70 mt-7 leading-relaxed max-w-2xl">
                    HeavenlyEats gives Latin restaurants a fully bilingual, fully branded
                    online ordering site — no card processing required. Your customers tap a
                    dish, you get a WhatsApp message, you cook, you get paid. That simple.
                </p>
                <div className="mt-9 flex flex-wrap gap-4">
                    <Link
                        to="/r/delicias"
                        data-testid="marketing-cta-demo-hero"
                        className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-orange-500 hover:bg-orange-400 text-white font-semibold shadow-xl shadow-orange-900/30 transition-all hover:scale-[1.02]"
                    >
                        Try the live demo
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <a
                        href="#pricing"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold transition-all"
                    >
                        See pricing
                    </a>
                </div>
            </motion.div>

            {/* Feature grid */}
            <section id="features" className="mt-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[
                    { icon: MessageCircle, title: "WhatsApp checkout", body: "Customers tap, you get a perfectly-formatted order in WhatsApp. No payment gateway required." },
                    { icon: Globe2, title: "Bilingual (EN / ES)", body: "Every label, every email, every receipt — auto-translated for the diaspora." },
                    { icon: Calendar, title: "Catering + calendar", body: "Plan multi-day prep, broadcast pickup reminders to whole days at once." },
                    { icon: ImageIcon, title: "AI menu photography", body: "Generate appetizing dish photos in seconds with Gemini Nano Banana." },
                    { icon: BarChart3, title: "Profitability dashboard", body: "See cost-per-dish, suggested prices, top sellers and 7-day revenue." },
                    { icon: Sparkles, title: "100% white-label", body: "Your brand, your colors, your country flag — even the chef bio. Zero HeavenlyEats branding on your site." },
                ].map((f, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.05 }}
                        className="p-6 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur"
                        data-testid={`marketing-feature-${i}`}
                    >
                        <div className="w-10 h-10 rounded-xl bg-orange-500/15 text-orange-300 flex items-center justify-center mb-4">
                            <f.icon className="w-5 h-5" />
                        </div>
                        <h3 className="font-serif font-bold text-xl mb-2">{f.title}</h3>
                        <p className="text-sm text-white/60 leading-relaxed">{f.body}</p>
                    </motion.div>
                ))}
            </section>

            {/* Pricing */}
            <section id="pricing" className="mt-24">
                <div className="text-center max-w-2xl mx-auto">
                    <h2 className="font-serif font-black text-4xl sm:text-5xl tracking-tight">Honest pricing. No card-on-file required.</h2>
                    <p className="text-white/60 mt-4">One-time setup, then a flat monthly fee. Cancel anytime after the 14-day trial.</p>
                </div>
                <div className="mt-12 grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
                    {[
                        { name: "Starter", price: 49, bullets: ["Bilingual storefront", "WhatsApp checkout", "Loyalty stamps", "Testimonials"], cta: "Start 14-day trial" },
                        { name: "Pro", price: 89, featured: true, bullets: ["Everything in Starter", "Catering flow + Calendar", "AI menu photos (Gemini)", "Email broadcasts", "Profitability dashboard"], cta: "Start 14-day trial" },
                    ].map((p, i) => (
                        <div
                            key={i}
                            data-testid={`marketing-plan-${p.name.toLowerCase()}`}
                            className={`p-7 rounded-2xl border ${p.featured ? "border-orange-400/50 bg-orange-500/[0.07] ring-1 ring-orange-400/30" : "border-white/10 bg-white/[0.03]"}`}
                        >
                            <div className="flex items-baseline justify-between">
                                <h3 className="font-serif font-bold text-2xl">{p.name}</h3>
                                {p.featured && <span className="text-[10px] uppercase tracking-widest font-bold text-orange-300">Most popular</span>}
                            </div>
                            <div className="mt-4 flex items-baseline gap-1">
                                <span className="text-5xl font-serif font-black">${p.price}</span>
                                <span className="text-white/50">/month</span>
                            </div>
                            <div className="text-xs text-white/50 mt-1">+ $299 one-time setup · 14-day free trial</div>
                            <ul className="mt-6 space-y-2 text-sm text-white/80">
                                {p.bullets.map((b, j) => (
                                    <li key={j} className="flex items-center gap-2"><span className="text-orange-300">▸</span>{b}</li>
                                ))}
                            </ul>
                            <button
                                disabled
                                data-testid={`marketing-plan-cta-${p.name.toLowerCase()}`}
                                title="Self-serve signup ships in Phase 6"
                                className={`mt-7 w-full px-5 py-3 rounded-full font-semibold transition-colors ${p.featured ? "bg-orange-500 hover:bg-orange-400 text-white" : "bg-white text-black hover:bg-white/90"} disabled:opacity-60 disabled:cursor-not-allowed`}
                            >
                                {p.cta} — coming soon
                            </button>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer */}
            <footer className="mt-24 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/50">
                <div>© {new Date().getFullYear()} HeavenlyEats · Built by HeavenlyAIsolutions</div>
                <div className="flex gap-5">
                    <Link to="/r/delicias" className="hover:text-white">Demo</Link>
                    <a href="mailto:hello@heavenlyai.solutions" className="hover:text-white">Contact</a>
                </div>
            </footer>
            <div className="mt-6 flex justify-center">
                <DevBranding variant="footer" />
            </div>
        </main>
    </div>
);

export default Marketing;
